import { useState, useEffect } from "react";

// Custom Animated Checkbox Component
const AnimatedCheckbox = ({ checked, onChange, label, t }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  
  const handleClick = () => {
    setIsAnimating(true);
    onChange(!checked);
    setTimeout(() => setIsAnimating(false), 300);
  };
  
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={handleClick}
          className="sr-only"
        />
        <div
          onClick={handleClick}
          className={`
            w-5 h-5 rounded-md border-2 transition-all duration-300 cursor-pointer
            flex items-center justify-center
            ${checked 
              ? 'bg-gradient-to-br from-[#B03982] to-[#733C86] border-[#B03982]' 
              : 'bg-white border-gray-300 group-hover:border-[#B03982]'
            }
            ${isAnimating ? 'scale-90' : 'scale-100'}
          `}
        >
          {checked && (
            <svg
              className="w-3.5 h-3.5 text-white animate-checkmark"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          )}
        </div>
        
        {/* Ripple Effect */}
        {isAnimating && (
          <div className="absolute inset-0 rounded-md animate-ripple bg-[#B03982]/20"></div>
        )}
      </div>
      
      <span className={`
        text-sm transition-all duration-300
        ${checked 
          ? 'text-[#B03982] font-medium' 
          : 'text-gray-600 group-hover:text-[#B03982]'
        }
      `}>
        {label || t("remember_me")}
      </span>
    </label>
  );
};

// Alternative: Pulsing Checkbox with Sparkle Effect
const PulsingCheckbox = ({ checked, onChange, label, t }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <label 
      className="flex items-center gap-3 cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onChange(!checked)}
          className="sr-only"
        />
        <div
          className={`
            w-5 h-5 rounded-md border-2 transition-all duration-300 cursor-pointer
            flex items-center justify-center relative overflow-hidden
            ${checked 
              ? 'bg-gradient-to-br from-[#B03982] to-[#733C86] border-[#B03982]' 
              : 'bg-white border-gray-300 group-hover:border-[#B03982]'
            }
          `}
        >
          {/* Animated Background Gradient */}
          {checked && (
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
          )}
          
          {/* Checkmark Icon */}
          {checked && (
            <svg
              className="w-3.5 h-3.5 text-white z-10 animate-check-bounce"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          )}
        </div>
        
        {/* Pulsing Ring Animation */}
        {checked && isHovered && (
          <div className="absolute inset-0 rounded-md animate-ping-ring bg-[#B03982]/30"></div>
        )}
      </div>
      
      <span className={`
        text-sm transition-all duration-300
        ${checked 
          ? 'text-[#B03982] font-semibold' 
          : 'text-gray-600 group-hover:text-[#B03982]'
        }
        ${isHovered && !checked ? 'translate-x-0.5' : ''}
      `}>
        {label || t("remember_me")}
      </span>
    </label>
  );
};

// Alternative: 3D Flip Checkbox
const FlipCheckbox = ({ checked, onChange, label, t }) => {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className="relative w-5 h-5 perspective-500">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onChange(!checked)}
          className="sr-only"
        />
        <div
          className={`
            absolute inset-0 w-full h-full transition-all duration-500 transform-style-3d
            ${checked ? 'rotate-y-180' : 'rotate-y-0'}
          `}
        >
          {/* Front Side */}
          <div className={`
            absolute inset-0 w-full h-full rounded-md border-2
            flex items-center justify-center backface-hidden
            ${checked 
              ? 'bg-gradient-to-br from-[#B03982] to-[#733C86] border-[#B03982] opacity-0' 
              : 'bg-white border-gray-300 group-hover:border-[#B03982]'
            }
          `}>
            <span className="text-xs text-gray-400">✓</span>
          </div>
          
          {/* Back Side */}
          <div className={`
            absolute inset-0 w-full h-full rounded-md
            bg-gradient-to-br from-[#B03982] to-[#733C86] border-2 border-[#B03982]
            flex items-center justify-center transform rotate-y-180 backface-hidden
            ${checked ? 'opacity-100' : 'opacity-0'}
          `}>
            <svg
              className="w-3.5 h-3.5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>
      </div>
      
      <span className={`
        text-sm transition-all duration-300
        ${checked 
          ? 'text-[#B03982] font-medium' 
          : 'text-gray-600 group-hover:text-[#B03982]'
        }
      `}>
        {label || t("remember_me")}
      </span>
    </label>
  );
};

// Alternative: Bouncy Checkbox with Spring Animation
const BouncyCheckbox = ({ checked, onChange, label, t }) => {
  const [spring, setSpring] = useState({ scale: 1, rotate: 0 });
  
  useEffect(() => {
    if (checked) {
      setSpring({ scale: 1.2, rotate: 0 });
      setTimeout(() => setSpring({ scale: 1, rotate: 0 }), 200);
    } else {
      setSpring({ scale: 1, rotate: 0 });
    }
  }, [checked]);
  
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onChange(!checked)}
          className="sr-only"
        />
        <div
          className={`
            w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer
            flex items-center justify-center
            ${checked 
              ? 'bg-gradient-to-br from-[#B03982] to-[#733C86] border-[#B03982]' 
              : 'bg-white border-gray-300 group-hover:border-[#B03982]'
            }
          `}
          style={{
            transform: `scale(${spring.scale}) rotate(${spring.rotate}deg)`,
            transition: 'transform 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          {checked && (
            <svg
              className="w-3.5 h-3.5 text-white animate-check-bounce"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          )}
        </div>
      </div>
      
      <span className={`
        text-sm transition-all duration-300
        ${checked 
          ? 'text-[#B03982] font-medium' 
          : 'text-gray-600 group-hover:text-[#B03982]'
        }
        ${checked ? 'translate-x-0.5' : ''}
      `}>
        {label || t("remember_me")}
      </span>
    </label>
  );
};

// Main Export: Choose your preferred style
export const CustomCheckbox = ({ checked, onChange, label, t, variant = "default" }) => {
  switch(variant) {
    case "pulse":
      return <PulsingCheckbox checked={checked} onChange={onChange} label={label} t={t} />;
    case "flip":
      return <FlipCheckbox checked={checked} onChange={onChange} label={label} t={t} />;
    case "bouncy":
      return <BouncyCheckbox checked={checked} onChange={onChange} label={label} t={t} />;
    default:
      return <AnimatedCheckbox checked={checked} onChange={onChange} label={label} t={t} />;
  }
};

// Add these styles to your global CSS or component
export const CheckboxStyles = () => (
  <style jsx>{`
    @keyframes checkmark {
      0% {
        stroke-dashoffset: 20;
        opacity: 0;
      }
      100% {
        stroke-dashoffset: 0;
        opacity: 1;
      }
    }
    
    @keyframes check-bounce {
      0% {
        transform: scale(0);
        opacity: 0;
      }
      50% {
        transform: scale(1.2);
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
    
    @keyframes ripple {
      0% {
        transform: scale(0.8);
        opacity: 0.6;
      }
      100% {
        transform: scale(2);
        opacity: 0;
      }
    }
    
    @keyframes shimmer {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
    
    @keyframes ping-ring {
      0% {
        transform: scale(1);
        opacity: 0.5;
      }
      100% {
        transform: scale(1.5);
        opacity: 0;
      }
    }
    
    .animate-checkmark {
      stroke-dasharray: 20;
      stroke-dashoffset: 20;
      animation: checkmark 0.3s ease forwards;
    }
    
    .animate-check-bounce {
      animation: check-bounce 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    }
    
    .animate-ripple {
      animation: ripple 0.3s ease-out;
    }
    
    .animate-shimmer {
      animation: shimmer 1s ease-in-out infinite;
    }
    
    .animate-ping-ring {
      animation: ping-ring 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    }
    
    .perspective-500 {
      perspective: 500px;
    }
    
    .transform-style-3d {
      transform-style: preserve-3d;
    }
    
    .rotate-y-0 {
      transform: rotateY(0deg);
    }
    
    .rotate-y-180 {
      transform: rotateY(180deg);
    }
    
    .backface-hidden {
      backface-visibility: hidden;
    }
  `}</style>
);